import clsx from "clsx";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useShallow } from "zustand/shallow";
import { useSignIn } from "@/hooks/useSignIn";
import { useWalletAddress } from "@/hooks/useWalletAddress";
import { useVerifyStore } from "@/stores/use-verify";
import { useFreeChest } from "../countdown/data";
import Spinner from "../spinner";
import { useSmash, useSmashV2 } from "./data";
import { useNeedVerify } from "./use-need-verify";
import { useVerify } from "./verify";
import { Reward } from "../reward";
import { useChestNumber } from "../chest-number/data";

export function ButtonOpenChest() {
  const navigate = useNavigate();
  const address = useWalletAddress();
  const { signIn, isLoading } = useSignIn();
  const { data, isLoading: isLoadingChest } = useChestNumber(address);

  const { t } = useTranslation();
  const { data: dataFreeChest, isLoading: isLoadingFreeChest } = useFreeChest();
  const { data: dataAPIVerify, isLoading: isLoadingAPIVerify } =
    useNeedVerify(address);

  const [signature, requestId, isLoadingVerify] = useVerifyStore(
    useShallow((state) => [
      state.signature,
      state.requestId,
      state.isLoading,
      state.verification_level,
    ])
  );

  const [txid, setTxId] = useState<string | undefined>();

  const { isLoading: isLoadingSmash, smash } = useSmash();
  const { isLoading: isLoadingSmashV2, smashV2 } = useSmashV2();

  const { verify } = useVerify();

  const handleOpenChest = useCallback(async () => {
    const isFree = !dataFreeChest?.isOpenFreeChest;
    if (dataAPIVerify?.data && !signature) {
      verify(isFree ? "smash" : "extraSmash");
      return;
    }
    if (signature) {
      const txid = await smash(signature, requestId);
      setTxId(txid);
      return;
    }

    const txid = await smashV2(isFree ? "smash" : "extraSmash");
    setTxId(txid);
  }, [
    verify,
    dataFreeChest,
    dataAPIVerify?.data,
    requestId,
    signature,
    smash,
    smashV2,
    navigate,
  ]);
  const buyChest = useMemo(() => {
    if (!dataFreeChest?.isOpenFreeChest) {
      return false;
    }
    if (!data) {
      return true;
    }
    return data.data.unusedPaymentCount <= 0;
  }, [dataFreeChest?.isOpenFreeChest, data]);

  if (!address) {
    return (
      <Button
        toggle={() => {
          signIn();
        }}
        disabled={isLoading}
      >
        {!isLoading ? (
          <span className="text-[16px] leading-[26px] font-bold uppercase select-none text-black">
            {t("signin")}
          </span>
        ) : (
          <Spinner />
        )}
      </Button>
    );
  }

  return (
    <>
      <OpenChest
        isLoading={
          isLoading ||
          isLoadingAPIVerify ||
          isLoadingFreeChest ||
          isLoadingVerify ||
          isLoadingSmashV2 ||
          isLoadingSmash ||
          isLoadingChest
        }
        toggle={handleOpenChest}
        isNeedVerify={!!dataAPIVerify?.data && !signature}
        isBuyChest={buyChest}
      />
      {txid ? (
        <Reward
          onClose={() => {
            setTxId(undefined);
          }}
          txid={txid}
        />
      ) : null}
    </>
  );
}

function OpenChest(props: {
  isLoading: boolean;
  toggle?: () => void;
  isNeedVerify: boolean;
  isBuyChest: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (props.isBuyChest) {
    return (
      <Button
        toggle={() => {
          navigate("./buy-chest");
        }}
        disabled={props.isLoading}
      >
        {!props.isLoading ? (
          <span className="text-[16px] leading-[26px] font-bold uppercase select-none text-black">
            {t("buy")}
          </span>
        ) : (
          <Spinner />
        )}
      </Button>
    );
  }

  return (
    <Button toggle={props.toggle} disabled={props.isLoading}>
      {!props.isLoading ? (
        <span className="text-[16px] leading-[26px] font-bold uppercase select-none text-black">
          {props.isNeedVerify ? t("verify") : t("smash")}
        </span>
      ) : (
        <Spinner />
      )}
    </Button>
  );
}

function Button(props: {
  disabled?: boolean;
  toggle?: () => void;
  children: ReactNode;
}) {
  const { disabled, toggle, children } = props;

  return (
    <button
      disabled={disabled}
      onClick={toggle}
      type="button"
      className={clsx(
        "flex items-center justify-center gap-2 h-[60px] btn-warning btn3d select-none",
        {
          "opacity-30": disabled,
        }
      )}
    >
      {children}
    </button>
  );
}
